import React, { useEffect } from 'react'
import { useSetAtom } from 'jotai'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { Box, RowsSkeletonLoader } from '@node-space/ui-components'
import { PathNames } from 'constants/General'
import { earnDetailsBreadcrumbAtom } from './atoms/breadcrumbAtom'
import { EarnAssetCustomerSummary } from './components/EarnAssetCustomerSummary'
import { EarnAssetDetailsHeader } from './components/EarnAssetDetailsHeader'
import { EarnAssetHistory } from './components/EarnAssetHistory'
import { EarnAssetMerchantSummary } from './components/EarnAssetMerchantSummary'
import { EarnAssetSummaryDirect } from './components/EarnAssetSummaryDirect'
import { EarnSheetProvider, useEarnSheetContext } from './context/EarnSheetProvider'
import { useEarnAssetDetailsQuery } from './hooks/api/useEarnAssetDetailsQuery'
import { useEarnStatisticsQuery } from './hooks/api/useEarnStatisticsQuery'
import { useEarnTracking } from './hooks/useEarnTracking'
import { useIsDirectEarning } from './hooks/useIsDirectEarning'
import { EARN_SHEET_ACTION_TYPE } from './types'
import { formatAPY, formatUsd } from './utils/utils'

export const EarnAssetDetails = () => (
  <EarnSheetProvider>
    <EarnAssetDetailsContent />
  </EarnSheetProvider>
)

const EarnAssetDetailsContent = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { portfolioId } = useParams<{ portfolioId: string }>()
  const setEarnDetailsBreadcrumb = useSetAtom(earnDetailsBreadcrumbAtom)
  const isDirectEarning = useIsDirectEarning()
  const { handleSheetOpen } = useEarnSheetContext()
  const { trackPageView } = useEarnTracking()

  const { data, isLoading } = useEarnAssetDetailsQuery(portfolioId)
  const { data: statistics, isLoading: isStatisticsLoading } = useEarnStatisticsQuery()

  useEffect(() => {
    setEarnDetailsBreadcrumb(data?.asset ?? '')
    return () => setEarnDetailsBreadcrumb('')
  }, [data?.asset, setEarnDetailsBreadcrumb])

  useEffect(() => {
    trackPageView('details', { portfolioId })
  }, [trackPageView, portfolioId])

  if (isLoading) {
    return <RowsSkeletonLoader rows={10} />
  }

  if (!data) {
    navigate(PathNames.NOT_FOUND)
    return null
  }

  const earnRate = formatAPY(data.myEarnRate ?? 0)
  const unallocatedFunds = formatUsd(
    statistics?.currencies[data.asset]?.merchantMovableBalance.amount
  )

  const handleDeposit = () => {
    handleSheetOpen({
      type: EARN_SHEET_ACTION_TYPE.DEPOSIT,
      header: t('earn.depositSheet.header'),
      selectedCurrency: data.asset,
      portfolioId,
      productName: data.productName,
    })
  }

  const handleWithdraw = () => {
    handleSheetOpen({
      type: EARN_SHEET_ACTION_TYPE.WITHDRAW,
      header: t('earn.withdrawSheet.header'),
      selectedCurrency: data.asset,
      portfolioId,
      productName: data.productName,
    })
  }

  if (isDirectEarning) {
    return (
      <>
        <EarnAssetDetailsHeader
          asset={data.asset}
          onWithdrawClick={handleWithdraw}
          onDepositClick={handleDeposit}
        />
        <Box flexCol gap={24}>
          <EarnAssetSummaryDirect
            earnRate={earnRate}
            allocatedFunds={formatUsd(data.fundsOverview.allocatedFunds)}
            myLifetimeEarnings={formatUsd(data.totalEarnings.fromMyAllocation)}
            unallocatedFunds={unallocatedFunds}
            isStatisticsLoading={isStatisticsLoading}
          />
          <EarnAssetHistory portfolioId={portfolioId} walletId={data.walletId} />
        </Box>
      </>
    )
  }

  return (
    <>
      <EarnAssetDetailsHeader
        asset={data.asset}
        onWithdrawClick={handleWithdraw}
        onDepositClick={handleDeposit}
      />
      <Box flexCol gap={24}>
        <Box flexCol gap={16} align="start" className=" lg:flex-row">
          <EarnAssetMerchantSummary
            earnRate={earnRate}
            allocatedFunds={formatUsd(data.fundsOverview.allocatedFunds)}
            allocationEarnings={formatUsd(data.totalEarnings.fromMyAllocation)}
            totalEarnings={formatUsd(data.totalEarnings.total)}
            customerEarnings={formatUsd(data.totalEarnings.fromCustomers)}
          />

          <EarnAssetCustomerSummary
            customerEarnRate={formatAPY(data.customerEarnRate ?? 0)}
            totalCustomerFundsEarning={formatUsd(data.fundsOverview.totalCustomerEligibleFunds)}
            distributedEarningsToCustomers={formatUsd(data.totalEarnings.fromCustomers)}
            epcTakesEarn={data.epcTakesEarn}
          />
        </Box>

        <EarnAssetHistory portfolioId={portfolioId} walletId={data.walletId} />
      </Box>
    </>
  )
}
